// Upsell contextual: traduz o recurso travado (ou o erro de limite vindo do
// banco) em texto e no plano que libera. Os limites reais vivem em
// @lucralink/shared (entitlements) e nos triggers SQL (20261003700001).

import { planTierLabel, requiredPlanFor, type GatedFeature, type Plan } from "@lucralink/shared";

export type GateReason = GatedFeature | "monitoredSources" | "forwardingDestinations" | "autoPromotions";

export interface GateCopy {
  readonly title: string;
  readonly description: string;
  readonly requiredPlan: Plan;
  readonly requiredLabel: string;
}

const COPY: Readonly<Record<GateReason, { title: string; description: string; plan?: Plan }>> = {
  fullAnalytics: { title: "Analytics completo é do PRO", description: "Veja cliques por canal, horários que mais vendem e suas comissões em um só lugar." },
  showcase: { title: "Vitrine é do PRO", description: "Publique uma página com suas ofertas para mandar no grupo e nas redes." },
  bioLink: { title: "Link na bio é do PRO", description: "Reúna seus links em uma página única para o Instagram e o TikTok." },
  shortener: { title: "Encurtador avulso é do PRO", description: "Encurte qualquer link com o domínio do LucraLink e acompanhe os cliques." },
  extension: { title: "Gerar links pela extensão é do PRO", description: "No PRO, a extensão gera links de afiliado direto da página do produto em qualquer loja. A captura de sessão do Mercado Livre continua liberada no Free." },
  customTemplates: { title: "Templates próprios são do PRO", description: "Crie seus próprios modelos de mensagem com as variáveis da oferta." },
  bulkSend: { title: "Disparos em massa são do PRO", description: "Agende e envie várias ofertas de uma vez para seus grupos." },
  watermark: { title: "Marca d'água é do PRO", description: "Coloque sua marca em todas as imagens enviadas automaticamente." },
  groupRotator: { title: "Rodízio de grupos é do PRO", description: "Divulgue um link só que distribui os cliques entre os seus grupos de WhatsApp." },
  aiImagesMonthly: { title: "50 imagens com IA por mês no ELITE", description: "Crie criativos de oferta com IA todo mês, com prompt personalizado." },
  monitoredSources: { title: "Limite de origens do Free atingido", description: "No Free você monitora 1 origem. No PRO, monitore quantas quiser.", plan: "pro" },
  forwardingDestinations: { title: "Limite de destinos atingido", description: "No Free você envia para 1 destino. No PRO, para até 10.", plan: "pro" },
  autoPromotions: { title: "Cota diária de promoções automáticas atingida", description: "No Free são 10 promoções automáticas por dia. No PRO, sem limite.", plan: "pro" },
};

function isGatedFeature(reason: GateReason): reason is GatedFeature {
  return !["monitoredSources", "forwardingDestinations", "autoPromotions"].includes(reason);
}

export function gateCopy(reason: GateReason): GateCopy {
  const entry = COPY[reason];
  const requiredPlan = entry.plan ?? (isGatedFeature(reason) ? requiredPlanFor(reason) : "pro");
  return { title: entry.title, description: entry.description, requiredPlan, requiredLabel: planTierLabel(requiredPlan) };
}

const FEATURE_BY_SQL: Readonly<Record<string, GatedFeature>> = {
  shortener: "shortener",
  custom_templates: "customTemplates",
  extension: "extension",
  bulk_send: "bulkSend",
  watermark: "watermark",
  full_analytics: "fullAnalytics",
  showcase: "showcase",
  bio_link: "bioLink",
  group_rotator: "groupRotator",
};

/** Mapeia o erro do banco (mensagem + detalhe) para o motivo do upsell. */
export function gateReasonFromError(error: unknown): GateReason | null {
  if (error === null || typeof error !== "object") return null;
  const { message, details } = error as { message?: unknown; details?: unknown };
  const text = typeof message === "string" ? message : "";
  if (text.includes("monitored_source_limit_reached")) return "monitoredSources";
  if (text.includes("forwarding_destination_limit_reached")) return "forwardingDestinations";
  if (text.includes("auto_promotion_daily_quota")) return "autoPromotions";
  if (text.includes("plan_feature_required")) {
    return FEATURE_BY_SQL[typeof details === "string" ? details : ""] ?? "fullAnalytics";
  }
  return null;
}
